// src/components/Finanzas/GestionEntregas.jsx
// Vista admin / responsable de cuenta maestra para crear y gestionar entregas
// de dinero a los bolsillos personales.

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Modal, Form, InputGroup } from 'react-bootstrap';
import { FaMoneyBillWave, FaPlus, FaBan, FaEye } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { Alertassw } from '../Alertas/Alertassw';
import {
  crearEntregaDinero,
  listarEntregas,
  anularEntrega,
  cuentasFuenteFondos,
  cuentasBolsillosPersonales
} from '../../api/finanzas';
import { useAuth } from '../../hooks/useAuth';
import { extractError } from './utils/extractError';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const ESTADO_BADGE = {
  PENDIENTE_CONFIRMACION: 'warning',
  CONFIRMANDO: 'info',
  CONFIRMADA: 'success',
  RECHAZADA: 'danger',
  ANULADA: 'secondary'
};

const GestionEntregas = () => {

  const { userData } = useAuth();
  const [entregas, setEntregas] = useState([]);
  const [cuentasMaestras, setCuentasMaestras] = useState([]);
  const [bolsillos, setBolsillos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todas');
  const [showCrear, setShowCrear] = useState(false);
  const [saving, setSaving] = useState(false);

  // entregadoPor no vive en el state — se lee del userData logueado al
  // momento de crear, evitando race conditions con la carga asincrona del JWT.
  const ESTADO_INICIAL_FORM = {
    cuentaOrigen: '',
    bolsilloDestino: '',
    monto: '',
    tipoPago: 'TRANSFERENCIA',
    descripcion: ''
  };
  const [form, setForm] = useState(ESTADO_INICIAL_FORM);

  useEffect(() => {
    cargar();
  }, [filtro]);

  const cargar = async () => {
    setLoading(true);
    try {
      const conditions = {};
      if (filtro !== 'todas') conditions.estado = filtro;

      const [ents, maestras, bols] = await Promise.all([
        listarEntregas({ conditions, sort: { fecha: -1 } }),
        cuentasFuenteFondos(),
        cuentasBolsillosPersonales()
      ]);

      if (ents?.status === 200) setEntregas(ents.data.data || []);
      if (maestras?.status === 200) setCuentasMaestras(maestras.data.data || []);
      if (bols?.status === 200) setBolsillos(bols.data.data || []);
    } catch (err) {
      console.error(err);
      Alertassw('error', 'Error', 'No se pudieron cargar las entregas');
    } finally {
      setLoading(false);
    }
  };

  const handleCrear = async () => {
    // entregadoPor se lee del usuario logueado al momento de crear (no del form)
    const entregadoPor = userData?._id;
    if (!entregadoPor) {
      Alertassw('error', 'Sesión no cargada', 'Recarga la página: tu sesión aún no se ha resuelto.');
      return;
    }
    if (!form.cuentaOrigen || !form.bolsilloDestino || !form.monto) {
      Alertassw('error', 'Campos requeridos', 'Origen, destino y monto son obligatorios');
      return;
    }
    if (Number(form.monto) <= 0) {
      Alertassw('error', 'Monto inválido', 'El monto debe ser mayor a 0');
      return;
    }

    // Buscar receptor (responsable del bolsillo destino)
    const bolsilloDestino = bolsillos.find(b => b._id === form.bolsilloDestino);
    if (!bolsilloDestino?.responsable) {
      Alertassw('error', 'Error', 'El bolsillo destino no tiene responsable asignado');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        cuentaOrigen: form.cuentaOrigen,
        bolsilloDestino: form.bolsilloDestino,
        receptor: bolsilloDestino.responsable._id || bolsilloDestino.responsable,
        monto: Number(form.monto),
        tipoPago: form.tipoPago,
        descripcion: form.descripcion,
        entregadoPor
      };
      const resp = await crearEntregaDinero(payload);
      if (resp?.status === 200 || resp?.status === 201) {
        Alertassw('success', 'Entrega creada', 'El receptor debe confirmarla para que se contabilice.');
        setShowCrear(false);
        setForm(ESTADO_INICIAL_FORM);
        cargar();
      }
    } catch (err) {
      Alertassw('error', 'Error', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const obtenerObservacion = (e) => {
    if (e.estado === 'CONFIRMADA') {
      return {
        titulo: 'Observación del receptor',
        icono: 'success',
        texto: e.confirmacion?.observacionesReceptor || '',
        fecha: e.confirmacion?.fechaConfirmacion
      };
    }
    if (e.estado === 'RECHAZADA') {
      return {
        titulo: 'Motivo del rechazo',
        icono: 'error',
        texto: e.rechazo?.motivoRechazo || '',
        fecha: e.rechazo?.fechaRechazo
      };
    }
    return null;
  };

  const tieneObservacion = (e) => {
    const info = obtenerObservacion(e);
    return Boolean(info && info.texto && String(info.texto).trim().length > 0);
  };

  const handleVerObservacion = (e) => {
    const info = obtenerObservacion(e);
    if (!info) return;
    const fechaTxt = info.fecha
      ? new Date(info.fecha).toLocaleString('es-CO')
      : '';
    const html = `
      <div style="text-align:left;">
        <p style="white-space:pre-wrap; margin-bottom:0.75rem;">${
          String(info.texto).replace(/</g, '&lt;')
        }</p>
        ${fechaTxt ? `<small class="text-muted">${fechaTxt}</small>` : ''}
      </div>
    `;
    Swal.fire({
      title: info.titulo,
      icon: info.icono,
      html,
      confirmButtonText: 'Cerrar'
    });
  };

  const handleAnular = async (e) => {
    const result = await Swal.fire({
      title: '¿Anular esta entrega?',
      input: 'textarea',
      inputLabel: 'Justificación',
      inputPlaceholder: 'Motivo de la anulación...',
      showCancelButton: true,
      confirmButtonText: 'Anular',
      confirmButtonColor: '#dc3545',
      cancelButtonText: 'Cancelar'
    });
    if (!result.isConfirmed) return;
    try {
      await anularEntrega(e._id, result.value);
      Alertassw('success', 'Anulada');
      cargar();
    } catch (err) {
      Alertassw('error', 'Error', extractError(err));
    }
  };

  return (
    <Container fluid className="py-4 w-100">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="text-primary fw-bold">
            <FaMoneyBillWave className="me-2" />
            Gestión de Entregas
          </h2>
          <p className="text-muted">Crea entregas de dinero desde las cuentas maestras hacia los bolsillos personales</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => setShowCrear(true)}>
            <FaPlus className="me-1" /> Nueva entrega
          </Button>
        </Col>
      </Row>

      {/* Filtros */}
      <Card className="mb-3 shadow-sm w-100">
        <Card.Body className='w-100'>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label>Filtrar por estado</Form.Label>
              <Form.Select value={filtro} onChange={(e) => setFiltro(e.target.value)}>
                <option value="todas">Todas</option>
                <option value="PENDIENTE_CONFIRMACION">Pendientes</option>
                <option value="CONFIRMADA">Confirmadas</option>
                <option value="RECHAZADA">Rechazadas</option>
                <option value="ANULADA">Anuladas</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button variant="outline-primary" onClick={cargar} className="w-100">Refrescar</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabla */}
      <Card className="shadow-sm w-100">
        <Card.Body className='w-100'>
          {loading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : entregas.length === 0 ? (
            <Alert variant="info" className="text-center mb-0">No hay entregas para este filtro.</Alert>
          ) : (
            <Table hover responsive>
              <thead className="table-light">
                <tr>
                  <th>Consecutivo</th>
                  <th>Fecha</th>
                  <th>Origen</th>
                  <th>Destino</th>
                  <th>Receptor</th>
                  <th>Tipo pago</th>
                  <th className="text-end">Monto</th>
                  <th className="text-center">Estado</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {entregas.map((e) => (
                  <tr key={e._id}>
                    <td><strong>{e.consecutivo || '—'}</strong></td>
                    <td>{new Date(e.fecha).toLocaleDateString('es-CO')}</td>
                    <td><small>{e.cuentaOrigen?.nombre || '—'}</small></td>
                    <td><small>{e.bolsilloDestino?.nombre || '—'}</small></td>
                    <td><small>{e.receptor?.nombre || '—'} {e.receptor?.apellido || '—'}</small></td>
                    <td><Badge bg="secondary">{e.tipoPago}</Badge></td>
                    <td className="text-end fw-bold">{formatoCOP(e.monto)}</td>
                    <td className="text-center">
                      <Badge bg={ESTADO_BADGE[e.estado] || 'secondary'}>{e.estado}</Badge>
                    </td>
                    <td className="text-center">
                      {tieneObservacion(e) && (
                        <Button variant="outline-info" size="sm" className="me-1"
                          onClick={() => handleVerObservacion(e)}
                          title={e.estado === 'CONFIRMADA' ? 'Ver observación del receptor' : 'Ver motivo del rechazo'}>
                          <FaEye />
                        </Button>
                      )}
                      {e.estado === 'PENDIENTE_CONFIRMACION' && (
                        <Button variant="outline-danger" size="sm"
                          onClick={() => handleAnular(e)}
                          title="Anular entrega">
                          <FaBan />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal crear entrega */}
      <Modal show={showCrear} onHide={() => setShowCrear(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Nueva entrega de dinero</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Cuenta origen (maestra) *</Form.Label>
            <Form.Select value={form.cuentaOrigen}
              onChange={(e) => setForm({ ...form, cuentaOrigen: e.target.value })}
            >
              <option value="">Selecciona...</option>
              {cuentasMaestras.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.nombre} ({c.codigo}) 
                </option>
              ))}
            </Form.Select>
            {cuentasMaestras.length === 0 && (
              <Form.Text className="text-warning">
                No hay cuentas marcadas como fuente de fondos. Edita una cuenta del PUC y activa <code>esFuenteFondos</code>.
              </Form.Text>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Bolsillo destino *</Form.Label>
            <Form.Select value={form.bolsilloDestino}
              onChange={(e) => setForm({ ...form, bolsilloDestino: e.target.value })}
            >
              <option value="">Selecciona...</option>
              {bolsillos.filter(b => b.activo).map((b) => (
                <option key={b._id} value={b._id}>
                  {b.nombre} ({b.responsable.nombre.charAt(0)} {b.responsable.apellido})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Row className="g-2">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Monto *</Form.Label>
                <InputGroup>
                  <InputGroup.Text>$</InputGroup.Text>
                  <Form.Control type="number" min="0"
                    value={form.monto}
                    onChange={(e) => setForm({ ...form, monto: e.target.value })}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Tipo de pago</Form.Label>
                <Form.Select value={form.tipoPago}
                  onChange={(e) => setForm({ ...form, tipoPago: e.target.value })}
                >
                  <option value="TRANSFERENCIA">Transferencia</option>
                  <option value="EFECTIVO">Efectivo</option>
                  <option value="CHEQUE">Cheque</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Descripción</Form.Label>
            <Form.Control as="textarea" rows={2}
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="Ej: Anticipo viaje Bogotá"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCrear(false)} disabled={saving}>Cancelar</Button>
          <Button variant="primary" onClick={handleCrear} disabled={saving}>
            {saving ? 'Creando...' : 'Crear entrega'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GestionEntregas;
